/**
 * The named colour palettes a week's colour can be picked from.
 *
 * The first palette is the default: the expedition map gives each week one
 * of its land colours in turn, and the plain map uses its accent. The rest
 * are offered in the editor as rows of swatches for a week's own colour.
 * Grounds are a pale tint of the accent on the maps' warm paper, so a land
 * stays coherent whatever its hue.
 */
import {mix} from './mapColor';

/** A land's colours. Everything else on the land is derived from these. */
export interface LandPalette {
  /** The land's ground tint. */
  ground: string;
  /** The land's accent: banner, coins, road. */
  accent: string;
}

/** A named palette. */
export interface RoadmapPalette {
  /** Stable id. */
  id: string;
  /** Shown in the editor. */
  name: string;
  /** One line on the mood, shown in the editor. */
  description: string;
  /** One land per week, cycled. */
  lands: readonly LandPalette[];
  /** The single accent of the plain map. */
  plainAccent: string;
}

/** The paper the grounds are tinted towards. */
const PAPER = '#f6f1e8';

/** How far the road's tint moves from the ground toward the accent. */
const ROAD_MIX = 0.22;

/**
 * The ground a land gets from its accent alone: a pale tint of the same hue.
 *
 * @param accent - The land's accent.
 * @return The ground colour.
 */
export function defaultGround(accent: string): string {
  return mix(accent, PAPER, 0.8);
}

/**
 * The road a land gets from its ground and accent: the ground, tinted a
 * little toward the accent.
 *
 * @param ground - The land's ground.
 * @param accent - The land's accent.
 * @return The road colour.
 */
export function defaultRoad(ground: string, accent: string): string {
  return mix(ground, accent, ROAD_MIX);
}

/**
 * Lands from accents alone, each on a pale tint of its own hue.
 *
 * @param accents - One accent per land.
 * @return The lands.
 */
function landsFrom(accents: readonly string[]): LandPalette[] {
  return accents.map(accent => ({accent, ground: defaultGround(accent)}));
}

/** Every palette, in the order the editor offers them. */
export const ROADMAP_PALETTES: readonly RoadmapPalette[] = [
  {
    id: 'stanford',
    name: 'Stanford',
    description: 'The secondary palette: Palo Alto, Lagunita, Poppy, Plum.',
    lands: [
      {ground: '#e3ebe4', accent: '#175e54'},
      {ground: '#dfeaee', accent: '#007c92'},
      {ground: '#f4e8d6', accent: '#d97a00'},
      {ground: '#e9e0ea', accent: '#620059'},
      {ground: '#f4e3dc', accent: '#c9452f'},
      {ground: '#dfece8', accent: '#279989'},
      {ground: '#efe1de', accent: '#8c1515'},
    ],
    plainAccent: '#8c1515',
  },
  {
    id: 'cardinal',
    name: 'Cardinal',
    description: 'Reds, bricks and warm stone. One family of colour.',
    lands: landsFrom([
      '#8c1515',
      '#651c32',
      '#a45e3a',
      '#6d3b48',
      '#b5563a',
      '#5d4b3c',
      '#8c1515',
    ]),
    plainAccent: '#8c1515',
  },
  {
    id: 'meadow',
    name: 'Meadow',
    description: 'Greens, olives and earth, like the foothills in spring.',
    lands: landsFrom([
      '#2f6b4f',
      '#5b7f3b',
      '#8a7a2a',
      '#3f7d6e',
      '#6e8b3d',
      '#4a6d3a',
      '#7c5a2e',
    ]),
    plainAccent: '#2f6b4f',
  },
  {
    id: 'ocean',
    name: 'Ocean',
    description: 'Teals and deep blues, cool and clear.',
    lands: landsFrom([
      '#0f6b7a',
      '#1f7fa3',
      '#2a9d8f',
      '#146c94',
      '#3a86a8',
      '#0e8a7c',
      '#0b5563',
    ]),
    plainAccent: '#0f6b7a',
  },
  {
    id: 'dusk',
    name: 'Dusk',
    description: 'Indigo, violet and slate blue, an evening sky.',
    lands: landsFrom([
      '#2f4b7c',
      '#5c4b8a',
      '#3a6a8f',
      '#6c3f7a',
      '#2c6e8a',
      '#4a4f9a',
      '#3c3a6e',
    ]),
    plainAccent: '#2f4b7c',
  },
  {
    id: 'sunrise',
    name: 'Sunrise',
    description: 'Coral, amber and rose, warm and bright.',
    lands: landsFrom([
      '#d9702b',
      '#c94f4f',
      '#c98a1e',
      '#c25c8a',
      '#e07b3f',
      '#b8452f',
      '#a63d5c',
    ]),
    plainAccent: '#c94f4f',
  },
  {
    id: 'slate',
    name: 'Slate',
    description: 'Muted greys and blue-greys, quiet and academic.',
    lands: landsFrom([
      '#4b5563',
      '#5b6b7a',
      '#6b6478',
      '#546e7a',
      '#607080',
      '#4e5d6c',
      '#3f4a58',
    ]),
    plainAccent: '#374151',
  },
];

/** The palette weeks are coloured from unless they choose their own colour. */
export const DEFAULT_PALETTE: RoadmapPalette = ROADMAP_PALETTES[0];
